import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BackLink } from "@/components/BackLink";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Hash, ArrowLeft, Shirt, RefreshCw, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachKitNumbers() {
  const [teams, setTeams] = useState([]);
  const [totals, setTotals] = useState({ assigned: 0, unassigned: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/kit/coach/squad-numbers`, { withCredentials: true });
      setTeams(r.data.teams || []);
      setTotals(r.data.totals || { assigned: 0, unassigned: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load squad numbers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-5 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Hash className="w-6 h-6 text-fuchsia-400" />
            <h1 className="text-2xl font-bold">Kit Numbers</h1>
          </div>
          <BackLink to="/academy/coach-dashboard" label="Back"
            className="text-slate-400 hover:text-white"
            testId="kit-numbers-back" />
        </div>
        <p className="text-sm text-slate-400 mb-3">
          Give every player in your squad a number — you don't have to wait for their kit to arrive.
          Numbers are unique inside an age group.
        </p>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40" data-testid="kit-numbers-assigned">
            {totals.assigned} with a number
          </Badge>
          <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40" data-testid="kit-numbers-unassigned">
            {totals.unassigned} still to do
          </Badge>
          <Button size="sm" variant="outline" onClick={load} className="h-9 ml-auto" data-testid="kit-numbers-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Link to="/academy/coach-kit-handover">
            <Button size="sm" variant="outline" className="h-9" data-testid="kit-numbers-to-handover">
              <Shirt className="w-4 h-4 mr-1" /> Handover
            </Button>
          </Link>
        </div>

        {loading ? (
          <p className="text-slate-400" data-testid="kit-numbers-loading">Loading…</p>
        ) : teams.length === 0 ? (
          <Card className="bg-slate-900/40 border-slate-700"><CardContent className="py-10 text-center text-slate-400" data-testid="kit-numbers-empty">
            You don't have any squads yet — ask an admin to assign you one.
          </CardContent></Card>
        ) : (
          <div className="space-y-5">
            {teams.map((t) => <TeamNumbers key={t.team_id} team={t} onSaved={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function TeamNumbers({ team, onSaved }) {
  const [filter, setFilter] = useState("");
  const [filling, setFilling] = useState(false);
  const players = (team.players || []).filter(
    (p) => !filter || (p.player_name || "").toLowerCase().includes(filter.toLowerCase())
  );
  const missing = (team.players || []).filter((p) => p.jersey_number == null).length;

  const autofill = async () => {
    if (!window.confirm(`Give the next free numbers to ${missing} player${missing === 1 ? "" : "s"} in ${team.team_name}?`)) return;
    setFilling(true);
    try {
      const r = await axios.post(`${API}/kit/coach/autofill-numbers`, { team_id: team.team_id }, { withCredentials: true });
      toast.success(r.data.message || `Numbered ${r.data.assigned_count} player${r.data.assigned_count === 1 ? "" : "s"}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-fill failed");
    } finally {
      setFilling(false);
    }
  };

  return (
    <Card className="bg-slate-900/40 border-slate-700" data-testid={`kit-numbers-team-${team.team_id}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          {team.team_name}
          <span className="text-xs text-slate-400 font-normal">{team.age_group || "—"}</span>
          {team.is_bucket && (
            <Badge className="bg-slate-700/60 text-slate-300 border-slate-600 text-[10px]" data-testid={`kit-numbers-bucket-${team.team_id}`}>
              bucket pool
            </Badge>
          )}
        </CardTitle>
        {team.free_numbers?.length > 0 && (
          <p className="text-xs text-slate-400" data-testid={`kit-numbers-free-${team.team_id}`}>
            Free in {team.age_group}: {team.free_numbers.slice(0, 12).join(", ")}…
          </p>
        )}
        {missing > 0 && (
          <Button
            size="sm" variant="outline"
            className="h-10 mt-2 border-emerald-500/40 text-emerald-200 w-full sm:w-auto"
            onClick={autofill}
            disabled={filling}
            data-testid={`kit-numbers-autofill-${team.team_id}`}
          >
            <Wand2 className="w-4 h-4 mr-1" />
            {filling ? "Numbering…" : `Auto-number ${missing} player${missing === 1 ? "" : "s"}`}
          </Button>
        )}
        {(team.players || []).length > 8 && (
          <Input
            className="bg-slate-900 border-slate-700 h-10 mt-2"
            placeholder="Filter players…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid={`kit-numbers-filter-${team.team_id}`}
          />
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {(team.players || []).length === 0 && (
          <p className="text-sm text-slate-400">No players in this squad yet.</p>
        )}
        {players.slice(0, 60).map((p) => (
          <PlayerNumberRow key={p.player_id} player={p} ageGroup={team.age_group} onSaved={onSaved} />
        ))}
        {players.length > 60 && (
          <p className="text-xs text-slate-500">Showing first 60 of {players.length} — use the filter to find a player.</p>
        )}
      </CardContent>
    </Card>
  );
}

function PlayerNumberRow({ player, ageGroup, onSaved }) {
  const [num, setNum] = useState(player.jersey_number ?? "");
  const [busy, setBusy] = useState(false);
  const saved = player.jersey_number ?? null;

  const save = async () => {
    const n = parseInt(num, 10);
    if (!n || n <= 0) { toast.error("Enter a number above 0"); return; }
    if (saved != null && !window.confirm(`${player.player_name} is currently #${saved}. Change to #${n}? #${saved} goes back into the pool.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/kit/coach/assign-jersey-by-player`, {
        player_id: player.player_id,
        age_group: ageGroup,
        jersey_number: n,
        override_existing: saved != null,
      }, { withCredentials: true });
      toast.success(`${player.player_name} is now #${n}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the number");
    } finally {
      setBusy(false);
    }
  };

  const swaps = player.size_swaps || [];

  return (
    <div className="bg-slate-800/40 rounded px-3 py-2" data-testid={`kit-numbers-row-${player.player_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-white truncate">
            {player.player_name}
            {saved != null && <span className="font-mono text-fuchsia-300 ml-2">#{saved}</span>}
          </p>
          <p className="text-[11px] text-slate-400">
            Ordered size {player.size_ordered || "—"}
            {player.sizes_issued?.length > 0 && ` · given ${player.sizes_issued.join(", ")}`}
          </p>
        </div>
        <Input
          type="number" inputMode="numeric" min="1"
          className="bg-slate-900 border-slate-700 h-10 w-20 text-base"
          placeholder="#"
          value={num}
          onChange={(e) => setNum(e.target.value)}
          data-testid={`kit-numbers-input-${player.player_id}`}
        />
        <Button
          size="sm" variant="outline"
          className="h-10 border-fuchsia-500/40 text-fuchsia-200"
          onClick={save}
          disabled={busy || String(num) === String(saved ?? "")}
          data-testid={`kit-numbers-save-${player.player_id}`}
        >
          {busy ? "Saving…" : saved != null ? "Update" : "Save"}
        </Button>
      </div>
      {swaps.length > 0 && (
        <p className="text-[11px] text-amber-300/90 mt-1" data-testid={`kit-numbers-swap-${player.player_id}`}>
          Size swap: ordered {swaps[swaps.length - 1].ordered_size} → given {swaps[swaps.length - 1].issued_size}
          {swaps[swaps.length - 1].reason ? ` (${swaps[swaps.length - 1].reason})` : ""}
        </p>
      )}
    </div>
  );
}
